import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import {
	Badge,
	Box,
	Button,
	Card,
	CardBody,
	CardHeader,
	Flex,
	SimpleGrid,
	Stack,
	Text,
	useColorModeValue,
	Wrap,
	WrapItem,
} from "@chakra-ui/react";
import moment from "moment";

import FinancialStatusOverviewTable from "../../Components/FinancialStatus/FinancialStatusOverviewTable";
import FinancialStatusWarnings from "../../Components/FinancialStatus/FinancialStatusWarnings";
import { formatAmount } from "../../utils/financialStatus";
import { buildFinancialStatusOverview, FINALIZATION_STATUSES } from "../../utils/financialStatusOverview";
import SearchBox from "../Properties/SearchBox";

export default function FinancialStatus() {
	const { t } = useTranslation();
	const [query, setQuery] = useState("");
	const [date, setDate] = useState([moment().year(), moment().month()]);
	const storeProperties = useSelector((state) => state.user?.user?.properties);
	const properties = useMemo(() => (storeProperties || []).filter((property) => !property.archived), [storeProperties]);

	const overview = useMemo(
		() => buildFinancialStatusOverview({ properties, year: date[0], monthIndex: date[1] }),
		[properties, date],
	);

	const panelBg = useColorModeValue("gray.50", "gray.700");
	const muted = useColorModeValue("gray.600", "gray.300");
	const statusCards = [
		{ key: "empty", label: t("properties.empty"), colorScheme: "blue" },
		{ key: "transferred", label: t("financialStatus.rentReceivedAndTransferred"), colorScheme: "green" },
		{ key: "received", label: t("financialStatus.rentReceivedNotTransferred"), colorScheme: "yellow" },
		{ key: "unpaid", label: t("financialStatus.rentNotReceived"), colorScheme: "red" },
	];

	return (
		<Card>
			<CardHeader>
				<Flex alignItems="center" justifyContent="space-between" gap={4} flexWrap="wrap">
					<Stack spacing={0}>
						<Text fontSize="xl" fontWeight="bold">
							{t("financialStatus.financialStatus")}
						</Text>
						<Text fontSize="sm" color={muted}>
							{overview.owners.length} owners · {overview.propertyCount} properties
						</Text>
					</Stack>
					<SearchBox handleSearch={setQuery} searchOptions={[{ label: "Name", description: "Search by owner name" }]} />
				</Flex>
			</CardHeader>
			<CardBody>
				<Stack spacing={4}>
					<Flex direction="row" alignItems="center" justifyContent="center" gap={4}>
						<Button size="sm" onClick={() => {
							const newDate = moment(date).subtract(1, "months");
							setDate([newDate.year(), newDate.month()]);
						}}>
							{t("table.previousMonth")}
						</Button>
						<Text fontWeight="bold" fontSize="lg" minW="10rem" textAlign="center">
							{moment(date).format("MMMM YYYY")}
						</Text>
						<Button
							size="sm"
							isDisabled={moment().isSame(moment(date), "year") && moment().isSame(moment(date), "month")}
							onClick={() => {
								const newDate = moment(date).add(1, "months");
								setDate([newDate.year(), newDate.month()]);
							}}
						>
							{t("table.nextMonth")}
						</Button>
					</Flex>
					<SimpleGrid columns={{ base: 2, lg: 4 }} spacing={3}>
						{statusCards.map((status) => (
							<Box key={status.key} bg={panelBg} borderWidth="1px" borderRadius="md" px={4} py={3}>
								<Flex justifyContent="space-between" alignItems="center" gap={2}>
									<Text fontSize="sm" color={muted}>{status.label}</Text>
									<Badge colorScheme={status.colorScheme} variant="subtle" fontSize="md" fontWeight="semibold">
										{overview.statusCounts[status.key]}
									</Badge>
								</Flex>
							</Box>
						))}
					</SimpleGrid>

					<Box bg={panelBg} borderWidth="1px" borderRadius="md" px={4} py={3}>
						<Wrap spacing={2} align="center">
							<WrapItem>
								<Text fontSize="sm" fontWeight="bold">
									{t("financialStatus.monthlyTotals")}:
								</Text>
							</WrapItem>
							{FINALIZATION_STATUSES.map((status) => (
								<WrapItem key={status}>
									<Badge variant="subtle">
										{t(`financialStatus.${status}`, { defaultValue: status })}:{" "}
										{formatAmount(overview.finalizedTotals[status].ron)} RON | {formatAmount(overview.finalizedTotals[status].eur)} EUR
									</Badge>
								</WrapItem>
							))}
						</Wrap>
					</Box>
					<FinancialStatusWarnings warnings={overview.warnings} />
					<FinancialStatusOverviewTable owners={overview.owners} query={query} />
				</Stack>
			</CardBody>
		</Card>
	);
}
